import { z } from "zod/v4";

export const statsFiltersSchema = z.object({
  country: z.string().optional(),
  categories: z.array(z.string()).optional(),
});

export const nameCountSchema = z.object({
  name: z.string(),
  count: z.number(),
});

export const unmetDemandItemSchema = z.object({
  name: z.string(),
  searchesCount: z.number(),
  upcomingCount: z.number(),
  kind: z.enum(["category", "location"]),
});

export const eventSourcesBreakdownSchema = z.object({
  publicWeb: z.number(),
  whatsappGroups: z.number(),
  facebookGroups: z.number(),
  instagram: z.number(),
  community: z.number(),
});

const overviewSchema = z.object({
  totalUsers: z.number(),
  active7d: z.number(),
  active30d: z.number(),
  totalUpcomingEvents: z.number(),
  totalSearches30d: z.number(),
  subscribed: z.number(),
});

export const publicStatsSchema = z.object({
  generatedAt: z.number(),
  filters: statsFiltersSchema,
  overview: overviewSchema,
  filtered: z.object({
    matchingEvents: z.number(),
    matchingAudience: z.number().nullable(),
    matchingSearches: z.number(),
  }),
  unmetDemand: z.array(unmetDemandItemSchema),
  events: z.object({
    upcoming: z.number(),
    sources: eventSourcesBreakdownSchema,
    userSubmitted: z.number(),
    apiSubmitted: z.number(),
    scraped: z.number(),
    byCategory: z.array(nameCountSchema),
    byCity: z.array(nameCountSchema),
  }),
  searches: z.object({
    total30d: z.number(),
    topCategories: z.array(nameCountSchema),
    topPerformers: z.array(nameCountSchema),
    topVenues: z.array(nameCountSchema),
  }),
  engagement: z.object({
    swipes30d: z.number(),
    likes30d: z.number(),
    clicks30d: z.number(),
  }),
  users: z.object({
    total: z.number(),
    active7d: z.number(),
    active30d: z.number(),
    subscribed: z.number(),
  }),
});

export const highlightEventSchema = z.object({
  id: z.string(),
  title: z.string(),
  category: z.string(),
  city: z.string(),
  venue: z.string(),
  start: z.number(),
  end: z.number(),
  soldOut: z.boolean(),
  price: z.string(),
  link: z.string(),
});

export const ticketTradeCountsSchema = z.object({
  ticketsListed: z.number(),
  buyersWaiting: z.number(),
});

export const producerWinSchema = z.object({
  event: z.string(),
  pageViews: z.number(),
  seenInChat: z.number(),
  pushed: z.number(),
});

export const statsHighlightsSchema = z.object({
  featuredEvents: z.array(highlightEventSchema),
  soldOutEvents: z.array(highlightEventSchema),
  ticketTrade: z.array(
    highlightEventSchema.extend(ticketTradeCountsSchema.shape),
  ),
  producerWins: z.array(producerWinSchema),
});

export const activeSourcesSchema = z.object({
  total: z.number(),
  instagram: z.number(),
  linkScrape: z.number(),
  whatsappGroups: z.number(),
});

export const apiStatsSchema = publicStatsSchema.extend({
  overview: overviewSchema.extend({
    newEvents7d: z.number(),
    newUsers7d: z.number(),
  }),
  activeSources: activeSourcesSchema,
  highlights: statsHighlightsSchema,
});

export type PublicStatsFilters = z.infer<typeof statsFiltersSchema>;
export type NameCount = z.infer<typeof nameCountSchema>;
export type UnmetDemandItem = z.infer<typeof unmetDemandItemSchema>;
export type EventSourcesBreakdown = z.infer<typeof eventSourcesBreakdownSchema>;
export type PublicStats = z.infer<typeof publicStatsSchema>;
export type HighlightEvent = z.infer<typeof highlightEventSchema>;
export type TicketTradeCounts = z.infer<typeof ticketTradeCountsSchema>;
export type TicketTradeHighlight = HighlightEvent & TicketTradeCounts;
export type ProducerWin = z.infer<typeof producerWinSchema>;
export type StatsHighlights = z.infer<typeof statsHighlightsSchema>;
export type ActiveSources = z.infer<typeof activeSourcesSchema>;
export type ApiStats = z.infer<typeof apiStatsSchema>;
